import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, UserGear, X } from "@phosphor-icons/react";
import { toast } from "sonner";

import { gmao } from "@/api";
import { formatApiError } from "@/lib/api";


function personName(person = {}) {
  return [person.firstName, person.lastName].filter(Boolean).join(" ") || person.email || person.id || "Utilisateur";
}

function Info({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-heading font-bold uppercase tracking-wide text-[var(--do-muted)]">{label}</dt>
      <dd className="mt-1 break-words text-sm font-medium text-[var(--do-ink)]">{value || "Non renseigné"}</dd>
    </div>
  );
}

export default function MachineSupportContextPanel({ machine, user }) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const role = user?.actualRole || user?.role;
  const canAssign = ["admin", "maintenance_manager"].includes(role);

  const loadContext = useCallback(async () => {
    if (!machine?.id) return;
    setLoading(true);
    try {
      const payload = await gmao.getMachineClientContext(machine.id);
      setContext(payload || null);
    } catch (requestError) {
      setContext(null);
      toast.error(formatApiError(requestError.response?.data?.detail) || "Impossible de charger les informations client.");
    } finally {
      setLoading(false);
    }
  }, [machine?.id]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const openEditor = async () => {
    if (!canAssign) return;
    try {
      const payload = await gmao.getMachineAssignmentOptions();
      const items = Array.isArray(payload?.items) ? payload.items : [];
      setOptions(items);
      setSelectedIds(Array.isArray(context?.assignedUserIds) ? context.assignedUserIds : []);
      setEditorOpen(true);
    } catch (requestError) {
      toast.error(formatApiError(requestError.response?.data?.detail) || "Impossible de charger les intervenants.");
    }
  };

  const grouped = useMemo(() => ({
    maintenanceManagers: (context?.assignees || []).filter((item) => item.role === "maintenance_manager"),
    technicians: (context?.assignees || []).filter((item) => item.role === "technician"),
  }), [context]);

  const client = context?.client || null;
  const clientName = client
    ? client.company || [client.firstName, client.lastName].filter(Boolean).join(" ") || client.email
    : "Aucun client affecté";
  const contactName = client
    ? [client.firstName, client.lastName].filter(Boolean).join(" ")
    : "";
  const save = async () => {
    setSaving(true);
    try {
      await gmao.updateMachineAssignees(machine.id, { user_ids: selectedIds });
      toast.success("Affectation de la machine mise à jour");
      setEditorOpen(false);
      await loadContext();
    } catch (requestError) {
      toast.error(formatApiError(requestError.response?.data?.detail) || "Affectation impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <section className="mt-4 lirah-card p-5 sm:p-6" data-testid="machine-support-context">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="label-eyebrow">Intervention</div>
            <h2 className="mt-1 font-heading text-lg font-black">Client et maintenance</h2>
          </div>
          {canAssign && (
            <button type="button" onClick={openEditor} className="inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-2.5 text-sm font-heading font-bold" data-testid="machine-assignees-edit">
              <UserGear size={18} weight="bold" /> Affecter
            </button>
          )}
        </div>

        {loading ? (
          <div className="mt-4 text-sm text-[var(--do-muted)]">Chargement…</div>
        ) : (
          <>
            <dl className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Info label="Client" value={clientName} />
              <Info label="Contact" value={contactName} />
              <Info label="Téléphone" value={client?.phone} />
              <Info label="E-mail" value={client?.email} />
            </dl>

            <div className="mt-5 grid gap-4 border-t border-[var(--do-border)] pt-4 sm:grid-cols-2">
              <div>
                <div className="text-xs font-heading font-bold uppercase tracking-wide text-[var(--do-muted)]">Responsable maintenance</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {grouped.maintenanceManagers.length ? grouped.maintenanceManagers.map((item) => (
                    <span key={item.id} className="rounded-full border border-[var(--do-border)] bg-[var(--do-bg-2)] px-3 py-1 text-xs font-bold">{personName(item)}</span>
                  )) : <span className="text-sm text-[var(--do-muted)]">Non affecté</span>}
                </div>
              </div>
              <div>
                <div className="text-xs font-heading font-bold uppercase tracking-wide text-[var(--do-muted)]">Technicien</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {grouped.technicians.length ? grouped.technicians.map((item) => (
                    <span key={item.id} className="rounded-full border border-[var(--do-border)] bg-[var(--do-bg-2)] px-3 py-1 text-xs font-bold">{personName(item)}</span>
                  )) : <span className="text-sm text-[var(--do-muted)]">Non affecté</span>}
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {editorOpen && (
        <div className="acta-modal-overlay fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-6" onMouseDown={saving ? undefined : () => setEditorOpen(false)}>
          <div className="acta-modal-surface max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 sm:p-6" onMouseDown={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="label-eyebrow">Affectation</div>
                <h2 className="mt-1 font-heading text-xl font-black">Techniciens et responsables maintenance</h2>
              </div>
              <button type="button" onClick={() => setEditorOpen(false)} disabled={saving} className="rounded-lg p-2"><X size={20} /></button>
            </div>

            <div className="mt-5 space-y-2">
              {options.map((item) => {
                const checked = selectedIds.includes(item.id);
                return (
                  <label key={item.id} className="flex cursor-pointer items-center gap-3 rounded-xl border border-[var(--do-border)] p-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setSelectedIds((current) => checked ? current.filter((id) => id !== item.id) : [...current, item.id])}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-bold">{personName(item)}</span>
                      <span className="text-xs text-[var(--do-muted)]">{item.role === "maintenance_manager" ? "Responsable maintenance" : "Technicien"}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end gap-2 border-t border-[var(--do-border)] pt-4">
              <button type="button" disabled={saving} onClick={() => setEditorOpen(false)} className="rounded-xl border border-[var(--do-border)] px-4 py-2.5 text-sm font-bold">Annuler</button>
              <button type="button" disabled={saving} onClick={save} className="inline-flex items-center gap-2 rounded-xl bg-[var(--do-accent)] px-4 py-2.5 text-sm font-bold text-[var(--do-on-accent)]">
                <Check size={17} weight="bold" /> {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
